import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Search, Gavel, CheckCircle2, RefreshCw } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { getApiUrl } from "@/utils/api";
import { IntegrationApiLogToggle } from "./IntegrationApiLogToggle";
import { useSiteStyles } from "@/hooks/useSiteStyles";
import PlanningAppealDetailModal from "@/components/shared/PlanningAppealDetailModal";

const API = getApiUrl();

export default function PlanningAppealsIntegrationSection() {
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [postcode, setPostcode] = useState("EN11 0RD");
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const [sectionEnabled, setSectionEnabled] = useState(true);
  const [savingFlag, setSavingFlag] = useState(false);
  const [selectedAppeal, setSelectedAppeal] = useState(null);
  const { refetch: refetchSharedSiteStyles } = useSiteStyles();

  const loadMeta = () => {
    axios.get(`${API}/admin/planning-appeals/meta`, { withCredentials: true })
      .then(({ data }) => setMeta(data))
      .catch((e) => console.error("Failed to load Planning Appeals meta:", e));
  };

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/planning-appeals/meta`, { withCredentials: true }),
      axios.get(`${API}/admin/site-styles`, { withCredentials: true }),
    ])
      .then(([metaRes, stylesRes]) => {
        setMeta(metaRes.data);
        setSectionEnabled((stylesRes.data?.styles || {}).planningAppealsSectionEnabled !== false);
      })
      .catch((e) => console.error("Failed to load Planning Appeals status:", e))
      .finally(() => setLoading(false));
  }, []);

  const handleToggle = async (checked) => {
    setSavingFlag(true);
    try {
      await axios.put(`${API}/admin/site-styles`, { styles: { planningAppealsSectionEnabled: checked } }, { withCredentials: true });
      setSectionEnabled(checked);
      refetchSharedSiteStyles();
      toast.success(checked ? "Enabled" : "Disabled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update setting");
    } finally {
      setSavingFlag(false);
    }
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/admin/integrations/planning-appeals-sync-now`, {}, { withCredentials: true });
      toast.success("Sync started in the background - this takes a few minutes for ~90k records.");
      setTimeout(loadMeta, 15000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start sync");
    } finally {
      setSyncing(false);
    }
  };

  const handleTest = async () => {
    if (!postcode.trim()) {
      toast.error("Enter a UK postcode to test");
      return;
    }
    setTesting(true);
    setResult(null);
    try {
      const { data } = await axios.get(`${API}/property-admin/planning-appeals/search`, {
        params: { postcode },
        withCredentials: true,
      });
      setResult(data);
      toast.success(`Found ${data.total} appeal(s)${data.outcode ? ` in postcode district ${data.outcode}` : ` matching "${data.postcode}"`}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test lookup failed");
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="planning-appeals-integration-loading">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="planning-appeals-integration-section">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Gavel className="h-4 w-4" />
            Planning Inspectorate - Appeals Database
          </CardTitle>
          <CardDescription>
            Free public data (no API key) - mirrored from the Planning Inspectorate's bulk
            Casework Database spreadsheet, since it has no live lookup API. Refreshed weekly
            via a scheduled job; you can also trigger a sync manually below.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2 text-sm" data-testid="planning-appeals-status">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <span className="font-medium">Connected</span>
            <span className="text-muted-foreground">
              {meta?.last_synced_at
                ? ` - ${(meta.total_count || 0).toLocaleString()} records, last synced ${new Date(meta.last_synced_at).toLocaleString("en-GB")}`
                : " - not synced yet"}
            </span>
          </div>

          <Button onClick={handleSyncNow} disabled={syncing} variant="outline" size="sm" data-testid="planning-appeals-sync-now-btn">
            {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Sync Now
          </Button>

          <div className="flex items-center justify-between gap-4 border-t border-border pt-4">
            <div>
              <Label className="font-normal text-sm">Show Planning Appeals section</Label>
              <p className="text-xs text-muted-foreground">Agent dashboard pill + Admin to Reports sub-tab</p>
            </div>
            {savingFlag ? (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            ) : (
              <Switch
                checked={sectionEnabled}
                onCheckedChange={handleToggle}
                data-testid="feature-planning-appeals-section-switch-integrations"
              />
            )}
          </div>

          <div className="space-y-2 border-t border-border pt-4">
            <Label htmlFor="planning-appeals-test-postcode" className="text-sm">Test lookup by postcode or street/house name</Label>
            <div className="flex items-center gap-2">
              <Input
                id="planning-appeals-test-postcode"
                value={postcode}
                onChange={(e) => setPostcode(e.target.value)}
                placeholder="e.g. EN11 0RD or Stow Park Drive"
                className="max-w-[240px]"
                data-testid="planning-appeals-test-postcode-input"
              />
              <Button onClick={handleTest} disabled={testing} size="sm" data-testid="planning-appeals-test-call-btn">
                {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                Test Lookup
              </Button>
            </div>
          </div>

          {result && (
            <div className="text-sm rounded-md p-3 border border-green-200 bg-green-50 text-green-800" data-testid="planning-appeals-test-result">
              <p className="font-medium mb-1">
                {result.total} appeal(s) found {result.outcode ? `in postcode district ${result.outcode}` : `matching "${result.postcode}"`}
              </p>
              {result.appeals.slice(0, 5).map((a) => (
                <p
                  key={a.case_number}
                  onClick={() => setSelectedAppeal(a)}
                  className="text-xs cursor-pointer hover:underline"
                  data-testid={`planning-appeals-test-result-row-${a.case_number}`}
                >
                  {a.decision_date?.slice(0, 10) || "-"} - {a.site_address} - <span className="font-medium">{a.decision}</span>
                </p>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <IntegrationApiLogToggle provider="planning_appeals" />

      <PlanningAppealDetailModal
        appeal={selectedAppeal}
        open={!!selectedAppeal}
        onOpenChange={(v) => !v && setSelectedAppeal(null)}
      />
    </div>
  );
}
